import React, { useState } from 'react'
import { cloneDeep } from 'lodash'

import { useAttributeOptions } from 'redux/hooks/AttributeOptions'

import {
  FilterConditionFormatBody,
  FilterPatchBody
} from 'types/GlobalKpiOption'
import type { TAttributeOptionDto } from '@homepal/api-contract'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'

import FilterEditItem from '../FilterEditItem'
import { useTranslation } from 'language/translator'
import Divider from 'components_new/atoms/Divider'

import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'

import { formatUpdateFilterBody, getFilterValue } from '../utils'

interface FilterGroupProps {
  allFilters: FilterConditionFormatBody[] // all filters in order to add,
  filterAttributeOptions: TAttributeOptionDto[]
  filters: FilterConditionFormatBody[] // filters in group
  groupId: string
  onClose: () => void
  updateFilters: (filters: FilterPatchBody[]) => void
}

const FilterGroup = (props: FilterGroupProps) => {
  const {
    allFilters,
    filterAttributeOptions,
    filters,
    groupId,
    onClose,
    updateFilters
  } = props

  const attributeOptions = useAttributeOptions()
  const { t, translateEnum } = useTranslation()

  /*-- drawer --*/
  const [drawer, setDrawer] = useState<{
    open: boolean
    title: string
  }>({
    open: false,
    title: ''
  })

  const handleOpenDrawer = () => {
    setDrawer({
      open: true,
      title: t('filters', 'filter_group')
    })
  }

  const handleBackDrawer = () => {
    setDrawer((prevState) => ({ ...prevState, open: false }))
  }

  const handleCloseDrawer = () => {
    handleBackDrawer()
    onClose()
  }

  const handleDelete = () => {
    const newFilters = cloneDeep(allFilters).filter(
      (f) => f.filter_group_id !== groupId
    )

    updateFilters(formatUpdateFilterBody(newFilters))
    handleBackDrawer()
  }

  return (
    <>
      <SettingsItem onClick={handleOpenDrawer}>
        <Box>
          {filters.map((filter, i) => {
            const attributeOption = filter.attribute_option.selected
              ? attributeOptions[filter.attribute_option.selected]
              : null

            return (
              <SettingsItem
                dividerTitle={<Chip label={t('filters', 'or')} size="small" />}
                key={`filter-${i}`}
                title={attributeOption?.name}
                value={getFilterValue(filter, t, translateEnum)}
              />
            )
          })}
        </Box>
      </SettingsItem>

      {/*-- drawer --*/}
      <SettingsDrawer
        open={drawer.open}
        onBack={handleBackDrawer}
        onClose={handleCloseDrawer}
        title={drawer.title}
      >
        {filters.map((filter, i) => (
          <>
            <FilterEditItem
              allFilters={allFilters}
              filterAttributeOptions={filterAttributeOptions}
              filter={filter}
              key={`filter-${i}`}
              noHelperText={true}
              updateFilters={updateFilters}
            />
            {i + 1 < filters.length ? (
              <Divider sx={{ mb: 2 }}>
                <Chip label={t('filters', 'or')} size="small" />
              </Divider>
            ) : null}
          </>
        ))}

        <SettingsGroup
          helperText={t('filters', 'filter_group_edit_helper_text')}
        >
          <SettingsItem
            color="error"
            onClick={handleDelete}
            title={t('global', 'delete')}
            variant="action"
          />
        </SettingsGroup>
      </SettingsDrawer>
    </>
  )
}

export default FilterGroup
